import { useState } from 'react';
import { logMeeting } from '../../../api/prospectApi';
import './StageMeetingLog.css';

const MEETING_TYPE_LABELS = {
  VIDEO_CALL:       'Video Call',
  IN_PERSON_MEETING:'In-Person',
  ONLINE_MEETING:   'Online',
};

function formatDate(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function todayISODate() {
  return new Date().toISOString().slice(0, 10);
}

export default function StageMeetingLog({ prospectId, stageKey, meetings, onJourneyUpdated, readOnly }) {
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('VIDEO_CALL');
  const [formDate, setFormDate] = useState(todayISODate());
  const [formDuration, setFormDuration] = useState('');
  const [formMinutes, setFormMinutes] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const sortedMeetings = [...(meetings || [])].sort((a, b) => {
    return new Date(b.meeting_at) - new Date(a.meeting_at);
  });

  function handleShowForm() {
    setShowForm(true);
    setFormError('');
    setFormType('VIDEO_CALL');
    setFormDate(todayISODate());
    setFormDuration('');
    setFormMinutes('');
  }

  function handleCancel() {
    setShowForm(false);
    setFormError('');
  }

  async function handleSubmit() {
    if (!formMinutes.trim() || formMinutes.trim().length < 10) {
      setFormError('Minutes must be at least 10 characters.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const body = {
        type: formType,
        meetingAt: formDate ? new Date(formDate).toISOString() : new Date().toISOString(),
        durationMinutes: formDuration ? parseInt(formDuration, 10) : null,
        minutes: formMinutes.trim(),
      };
      const updated = await logMeeting(prospectId, stageKey, body);
      onJourneyUpdated(updated);
      setShowForm(false);
    } catch (ex) {
      setFormError(ex.message || 'Failed to log meeting.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="sml__section">
      <div className="sml__header">
        <span className="sml__heading">📅 Meetings</span>
        {!showForm && !readOnly && (
          <button className="btn--outline sml__log-btn" onClick={handleShowForm}>
            + Log Meeting
          </button>
        )}
      </div>

      {showForm && (
        <div className="sml__form">
          <div className="sml__form-row">
            <div className="sml__form-field">
              <label className="sml__label">Meeting Date</label>
              <input
                type="date"
                className="sml__input"
                value={formDate}
                onChange={e => setFormDate(e.target.value)}
              />
            </div>
            <div className="sml__form-field">
              <label className="sml__label">Type</label>
              <select className="sml__input" value={formType} onChange={e => setFormType(e.target.value)}>
                <option value="VIDEO_CALL">Video Call</option>
                <option value="IN_PERSON_MEETING">In-Person Meeting</option>
                <option value="ONLINE_MEETING">Online Meeting</option>
              </select>
            </div>
            <div className="sml__form-field sml__form-field--narrow">
              <label className="sml__label">Duration (min)</label>
              <input
                type="number"
                className="sml__input"
                value={formDuration}
                onChange={e => setFormDuration(e.target.value)}
                placeholder="e.g. 45"
                min="1"
              />
            </div>
          </div>
          <div className="sml__form-field">
            <label className="sml__label">Minutes <span className="sml__required">*</span></label>
            <textarea
              className="sml__textarea"
              value={formMinutes}
              onChange={e => setFormMinutes(e.target.value)}
              placeholder="Record discussion points, decisions, and follow-up actions..."
              rows={5}
            />
          </div>
          {formError && <div className="sml__form-error">{formError}</div>}
          <div className="sml__form-actions">
            <button className="btn--primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Meeting'}
            </button>
            <button className="btn--outline" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {sortedMeetings.length === 0 && !showForm && (
        <div className="sml__empty">No meetings logged yet.</div>
      )}

      <div className="sml__list">
        {sortedMeetings.map(m => (
          <div key={m.meeting_id} className="sml__entry">
            <div className="sml__entry-header">
              <span className="sml__entry-date">{formatDate(m.meeting_at)}</span>
              <span className={`sml__type-chip sml__type-chip--${(m.type || '').toLowerCase().replace(/_/g, '-')}`}>
                {MEETING_TYPE_LABELS[m.type] || m.type}
              </span>
              {m.duration_minutes && (
                <span className="sml__duration">{m.duration_minutes} min</span>
              )}
            </div>
            <div className="sml__entry-minutes">{m.minutes}</div>
            <div className="sml__entry-logged">Logged {formatDate(m.logged_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
